import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { sendMerchantOtp, verifyMerchantOtp, merchantSignup } from '../../services/api';
import { showSuccessToast, showErrorToast, showLoadingToast, updateToast, showInfoToast } from '../../utils/toast';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  InputAdornment,
} from '@mui/material';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import SecurityIcon from '@mui/icons-material/Security';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

// ── Stats data shown on the left hero panel ──────────────────────────────────
const STATS = [
  { value: '10K+', label: 'Registered Merchants' },
  { value: '₹500Cr+', label: 'Monthly Transactions' },
  { value: '99.9%', label: 'Uptime SLA' },
];

// ── Left hero panel ───────────────────────────────────────────────────────────
const HeroPanel = () => (
  <Box
    sx={{
      flex: 1,
      minHeight: { xs: 'auto', md: '100vh' },
      display: { xs: 'none', md: 'flex' },
      flexDirection: 'column',
      justifyContent: 'space-between',
      p: { md: 5, lg: 7 },
      position: 'relative',
      overflow: 'hidden',
      background: 'linear-gradient(160deg, #060d1e 0%, #081528 50%, #060d1e 100%)',
    }}
  >
    {/* Subtle grid */}
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        backgroundImage:
          'linear-gradient(rgba(6,182,212,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(6,182,212,0.06) 1px, transparent 1px)',
        backgroundSize: '56px 56px',
        pointerEvents: 'none',
      }}
    />

    {/* Glow blobs */}
    {[
      { size: 420, top: '-10%', left: '-12%', color: 'rgba(6,182,212,0.18)' },
      { size: 320, bottom: '5%', right: '-10%', color: 'rgba(59,130,246,0.15)' },
    ].map((b, i) => (
      <Box
        key={i}
        sx={{
          position: 'absolute',
          width: b.size,
          height: b.size,
          top: b.top,
          left: b.left,
          bottom: b.bottom,
          right: b.right,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${b.color} 0%, transparent 70%)`,
          filter: 'blur(2px)',
          pointerEvents: 'none',
          animation: `heroPulse${i} ${i === 0 ? 8 : 10}s ease-in-out infinite`,
          [`@keyframes heroPulse${i}`]: {
            '0%, 100%': { transform: 'scale(1)' },
            '50%': { transform: 'scale(1.08)' },
          },
        }}
      />
    ))}

    {/* Brand logo */}
    <Box sx={{ position: 'relative', zIndex: 1, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Box 
        component="img" 
        src="/payntric_logo.png" 
        alt="PayNTric Logo" 
        sx={{ 
          height: 56, 
          width: 'auto',
          filter: 'drop-shadow(0 0 12px rgba(6,182,212,0.55))',
        }} 
      />
    </Box>

    {/* Hero headline */}
    <Box sx={{ position: 'relative', zIndex: 1, my: 'auto', pt: 6 }}>
      <Typography
        sx={{
          fontSize: { xs: '1.8rem', sm: '2.2rem', lg: '2.8rem' },
          fontWeight: 800,
          color: '#f1f5f9',
          lineHeight: 1.2,
          mb: 2,
          fontFamily: "'Inter', sans-serif",
        }}
      >
        Welcome to the{' '}
        <Box
          component="span"
          sx={{
            background: 'linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          Future of
          <br />
          Digital Payments
        </Box>
      </Typography>

      <Typography sx={{ fontSize: '0.95rem', color: '#64748b', lineHeight: 1.7, maxWidth: 380 }}>
        Manage your API gateway, track transactions, and grow your business — all from one powerful dashboard.
      </Typography>
    </Box>

    {/* Stats row */}
    <Box sx={{ position: 'relative', zIndex: 1 }}>
      <Box
        sx={{
          display: 'flex',
          gap: { md: 3, lg: 4 },
          pt: 4,
          borderTop: '1px solid rgba(99,102,241,0.15)',
        }}
      >
        {STATS.map((s) => (
          <Box key={s.label}>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: '1.3rem',
                color: '#67e8f9',
                fontFamily: "'Inter', sans-serif",
                lineHeight: 1,
              }}
            >
              {s.value}
            </Typography>
            <Typography sx={{ fontSize: '0.72rem', color: '#475569', mt: 0.4, fontWeight: 500 }}>
              {s.label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  </Box>
);

// ── Shared input style ────────────────────────────────────────────────────────
const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    background: '#fafbff',
    fontSize: '0.95rem',
    transition: 'all 0.2s ease',
    '& fieldset': { borderColor: '#bae6fd', borderWidth: '1.5px' },
    '&:hover fieldset': { borderColor: '#06b6d4', borderWidth: '1.5px' },
    '&.Mui-focused fieldset': { borderColor: '#06b6d4', borderWidth: '2px' },
    '&.Mui-focused': { background: '#fff', boxShadow: '0 0 0 4px rgba(6,182,212,0.10)' },
    '&.Mui-disabled': { background: '#f8fafc' },
  },
  '& .MuiInputLabel-root': { color: '#94a3b8', fontSize: '0.9rem' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#06b6d4' },
  mb: 0.5,
};

// ── Terms checkbox (reused on login + signup) ─────────────────────────────────
const TermsCheckbox = ({ checked, onChange, onNavigate }) => (
  <FormControlLabel
    control={
      <Checkbox
        checked={checked}
        onChange={onChange}
        sx={{
          color: '#cbd5e1',
          '&.Mui-checked': { color: '#06b6d4' },
          padding: '6px',
        }}
        icon={
          <Box
            sx={{
              width: 19,
              height: 19,
              borderRadius: '5px',
              border: '1.5px solid #cbd5e1',
              background: '#fff',
            }}
          />
        }
        checkedIcon={
          <Box
            sx={{
              width: 19,
              height: 19,
              borderRadius: '5px',
              background: 'linear-gradient(135deg, #06b6d4, #3b82f6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CheckCircleOutlineIcon sx={{ color: '#fff', fontSize: 14 }} />
          </Box>
        }
      />
    }
    label={
      <Typography sx={{ fontSize: '0.78rem', color: '#64748b', lineHeight: 1.6 }}>
        I agree to the{' '}
        <Box component="span" onClick={() => onNavigate('/terms')} sx={{ color: '#6366f1', fontWeight: 600, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>Terms & Conditions</Box>
        ,{' '}
        <Box component="span" onClick={() => onNavigate('/privacy')} sx={{ color: '#6366f1', fontWeight: 600, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>Privacy Policy</Box>
        {' '} & {' '}
        <Box component="span" onClick={() => onNavigate('/refund')} sx={{ color: '#6366f1', fontWeight: 600, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>Refund Policy</Box>
      </Typography>
    }
    sx={{ alignItems: 'flex-start', mx: 0, mt: 2 }}
  />
);

// ── Main component ────────────────────────────────────────────────────────────
const MuiLogin = () => {
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [error, setError] = useState('');
  const [showSignup, setShowSignup] = useState(false);
  const [signupData, setSignupData] = useState({
    merchantName: '',
    email: '',
    companyName: '',
    companyAddress: '',
  });
  const [tempMobileNumber, setTempMobileNumber] = useState('');
  const [currentOtp, setCurrentOtp] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();

  // ── Handlers (logic completely unchanged) ────────────────────────────────────

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const toastId = showLoadingToast('Sending OTP...');
    try {
      await sendMerchantOtp(phone);
      setOtpSent(true);
      updateToast(toastId, 'OTP sent successfully', 'success');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send OTP');
      updateToast(toastId, err.response?.data?.message || 'Failed to send OTP', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const toastId = showLoadingToast('Verifying OTP...');
    try {
      const response = await verifyMerchantOtp(phone, otp);
      if (response.data.newUser) {
        updateToast(toastId, 'Preparing new OTP for registration...', 'info');
        await sendMerchantOtp(phone, 'SIGNUP');
        setTempMobileNumber(phone);
        setShowSignup(true);
        setCurrentOtp('');
        updateToast(toastId, 'New OTP sent for registration. Please check your messages and use the latest OTP.', 'success');
        setTimeout(() => {
          const firstField = document.querySelector('input[name="merchantName"]');
          if (firstField) firstField.focus();
        }, 100);
      } else {
        if (response.data.accessToken && response.data.merchantId) {
          const userData = { ...response.data, role: 'MERCHANT' };
          login(userData, response.data.accessToken);
          navigate('/merchant');
          updateToast(toastId, 'Login successful', 'success');
        } else {
          updateToast(toastId, 'Invalid login response. Please try again.', 'error');
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid OTP');
      updateToast(toastId, err.response?.data?.message || 'Invalid OTP', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const toastId = showLoadingToast('Creating account...');
    try {
      await new Promise((resolve) => setTimeout(resolve, 3000));
      const requestData = { mobileNumber: tempMobileNumber, otp: currentOtp, ...signupData };
      const response = await merchantSignup(requestData);
      const userData = { ...response.data, role: 'MERCHANT' };
      login(userData, response.data.accessToken);
      navigate('/merchant');
      updateToast(toastId, 'Registration successful!', 'success');
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Registration failed';
      setError(errorMessage);
      updateToast(toastId, errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleRestartOtp = () => {
    setShowSignup(false);
    setOtpSent(false);
    setOtp('');
    setTempMobileNumber('');
    setError('');
    setPhone('');
    setSignupData({ merchantName: '', email: '', companyName: '', companyAddress: '' });
    showInfoToast('Please request a new OTP to continue');
  };

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        background: '#fff',
        fontFamily: "'Inter', sans-serif",
      }}
    >
      {/* LEFT — Hero panel */}
      <HeroPanel />

      {/* RIGHT — Form panel */}
      <Box
        sx={{
          width: { xs: '100%', md: '460px', lg: '500px' },
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          background: 'linear-gradient(160deg, #f8fafc 0%, #ffffff 60%, #f0fdfe 100%)',
          boxShadow: { md: '-4px 0 40px rgba(0,0,0,0.08)' },
          position: 'relative',
          overflowY: 'auto',
          borderLeft: '1px solid rgba(6,182,212,0.1)',
        }}
      >
        {/* Inner scroll wrapper — vertically centered */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            px: { xs: 2.5, sm: 5, md: 6 },
            py: { xs: 4, md: 6 },
            minHeight: { xs: 'auto', md: '100vh' },
          }}
        >
          {/* Mobile brand (only visible on small screens) */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 1.5, mb: 4 }}>
            <Box 
              component="img" 
              src="/payntric_logo.png" 
              alt="PayNTric Logo" 
              sx={{ 
                height: 28, 
                width: 'auto',
                filter: 'drop-shadow(0 0 8px rgba(6,182,212,0.4))',
              }} 
            />
            <Box>
              <Typography
                sx={{
                  fontWeight: 900,
                  fontSize: '1.4rem',
                  background: 'linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                PayNTric
              </Typography>
              <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', letterSpacing: '1.2px', textTransform: 'uppercase', lineHeight: 1 }}>
                Payment Gateway Portal
              </Typography>
            </Box>
          </Box>



          {/* ── LOGIN / OTP form ─────────────────────────────────────── */}
          {!showSignup && (
            <>
              {/* Title */}
              <Box sx={{ mb: { xs: 3, md: 4 } }}>
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: { xs: '1.6rem', md: '1.9rem' },
                    color: '#0f172a',
                    lineHeight: 1.2,
                    fontFamily: "'Inter', sans-serif",
                  }}
                >
                  Get Started
                </Typography>
                <Typography sx={{ color: '#94a3b8', fontSize: { xs: '0.85rem', md: '0.9rem' }, mt: 1 }}>
                  {otpSent
                    ? `OTP sent to +91 ${phone}`
                    : 'Enter your mobile number to proceed'}
                </Typography>
              </Box>

              {error && (
                <Alert
                  severity="error"
                  sx={{ mb: 2.5, borderRadius: '10px', fontSize: '0.85rem' }}
                >
                  {error}
                </Alert>
              )}

              <Box component="form" onSubmit={otpSent ? handleVerifyOtp : handleSendOtp}>
                <TextField
                  fullWidth
                  label="Mobile Number"
                  placeholder="Enter 10-digit mobile number"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  margin="normal"
                  required
                  disabled={otpSent}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneAndroidIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={inputSx}
                />

                {otpSent && (
                  <TextField
                    fullWidth
                    label="One-Time Password"
                    placeholder="Enter 6-digit OTP"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    margin="normal"
                    required
                    autoFocus
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SecurityIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={inputSx}
                  />
                )}

                {/* Terms – only before first send */}
                {!otpSent && (
                  <TermsCheckbox
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    onNavigate={navigate}
                  />
                )}

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading || (!otpSent && !termsAccepted)}
                  sx={{
                    mt: 3,
                    mb: 1.5,
                    py: 1.6,
                    borderRadius: '10px',
                    textTransform: 'none',
                    fontSize: '1rem',
                    fontWeight: 700,
                    letterSpacing: '0.3px',
                    background: 'linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%)',
                    boxShadow: '0 4px 20px rgba(6,182,212,0.30)',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #0891b2 0%, #2563eb 100%)',
                      boxShadow: '0 6px 28px rgba(6,182,212,0.42)',
                      transform: 'translateY(-1px)',
                    },
                    '&.Mui-disabled': {
                      background: '#e2e8f0',
                      color: '#94a3b8',
                      boxShadow: 'none',
                    },
                  }}
                  endIcon={!loading && <ArrowForwardIcon sx={{ fontSize: 18 }} />}
                >
                  {loading ? (
                    <CircularProgress size={22} color="inherit" />
                  ) : otpSent ? (
                    'Verify & Login'
                  ) : (
                    'Proceed'
                  )}
                </Button>

                {otpSent && (
                  <Button
                    type="button"
                    fullWidth
                    variant="text"
                    size="small"
                    disabled={loading}
                    startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
                    onClick={() => { setOtpSent(false); setOtp(''); setError(''); }}
                    sx={{
                      textTransform: 'none',
                      fontSize: '0.84rem',
                      color: '#64748b',
                      borderRadius: '8px',
                      '&:hover': { color: '#06b6d4', background: 'rgba(6,182,212,0.07)' },
                    }}
                  >
                    Change Mobile Number
                  </Button>
                )}
              </Box>
            </>
          )}

          {/* ── SIGNUP form ──────────────────────────────────────────── */}
          {showSignup && (
            <>
              <Box sx={{ mb: { xs: 2, md: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                  <Box
                    sx={{
                      width: { xs: 32, md: 36 },
                      height: { xs: 32, md: 36 },
                      borderRadius: '10px',
                      background: 'linear-gradient(135deg, #06b6d4, #3b82f6)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <PersonAddAltIcon sx={{ color: '#fff', fontSize: { xs: 17, md: 19 } }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.4rem', md: '1.6rem' }, color: '#0f172a', lineHeight: 1.2 }}>
                      Create Account
                    </Typography>
                    <Typography sx={{ fontSize: '0.78rem', color: '#94a3b8', mt: 0.2 }}>
                      +91 {tempMobileNumber}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {error && (
                <Alert severity="error" sx={{ mb: 2, borderRadius: '10px', fontSize: '0.85rem' }}>
                  {error}
                </Alert>
              )}

              <Alert
                severity="info"
                sx={{
                  mb: 2,
                  borderRadius: '10px',
                  background: '#f0f4ff',
                  border: '1px solid #c7d2fe',
                  color: '#4338ca',
                  '& .MuiAlert-icon': { color: '#6366f1' },
                  fontSize: '0.82rem',
                }}
              >
                A new OTP has been sent to your mobile. Enter it below to complete registration.
              </Alert>

              <Box component="form" onSubmit={handleSignup}>
                <TextField
                  fullWidth label="Registration OTP" placeholder="6-digit OTP"
                  value={currentOtp}
                  onChange={(e) => setCurrentOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  margin="normal" required autoFocus
                  InputProps={{ startAdornment: <InputAdornment position="start"><SecurityIcon sx={{ color: '#94a3b8', fontSize: 20 }} /></InputAdornment> }}
                  sx={inputSx}
                />
                <TextField
                  fullWidth label="Full Name" name="merchantName"
                  value={signupData.merchantName}
                  onChange={(e) => setSignupData({ ...signupData, merchantName: e.target.value })}
                  margin="normal" required sx={inputSx}
                />
                <TextField
                  fullWidth label="Email Address" type="email"
                  value={signupData.email}
                  onChange={(e) => setSignupData({ ...signupData, email: e.target.value })}
                  margin="normal" required sx={inputSx}
                />
                <TextField
                  fullWidth label="Company Name"
                  value={signupData.companyName}
                  onChange={(e) => setSignupData({ ...signupData, companyName: e.target.value })}
                  margin="normal" required sx={inputSx}
                />
                <TextField
                  fullWidth label="Company Address" multiline rows={2}
                  value={signupData.companyAddress}
                  onChange={(e) => setSignupData({ ...signupData, companyAddress: e.target.value })}
                  margin="normal" required sx={inputSx}
                />

                <TermsCheckbox
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  onNavigate={navigate}
                />

                <Button
                  type="submit" fullWidth variant="contained" size="large"
                  disabled={loading || !termsAccepted}
                  sx={{
                    mt: 2.5, mb: 1.5, py: 1.6, borderRadius: '10px', textTransform: 'none',
                    fontSize: '1rem', fontWeight: 700,
                    background: 'linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%)',
                    boxShadow: '0 4px 20px rgba(6,182,212,0.30)',
                    '&:hover': { background: 'linear-gradient(135deg, #0891b2 0%, #2563eb 100%)', transform: 'translateY(-1px)' },
                    '&.Mui-disabled': { background: '#e2e8f0', color: '#94a3b8', boxShadow: 'none' },
                    transition: 'all 0.2s ease',
                  }}
                >
                  {loading ? <CircularProgress size={22} color="inherit" /> : 'Complete Registration'}
                </Button>

                <Button type="button" fullWidth variant="text" size="small" disabled={loading}
                  onClick={handleRestartOtp}
                  sx={{ textTransform: 'none', fontSize: '0.82rem', color: '#06b6d4', mb: 0.5, borderRadius: '8px', '&:hover': { background: 'rgba(6,182,212,0.07)' } }}
                >
                  Resend OTP
                </Button>
                <Button type="button" fullWidth variant="text" size="small" disabled={loading}
                  startIcon={<ArrowBackIcon sx={{ fontSize: 15 }} />}
                  onClick={() => { setShowSignup(false); setOtpSent(false); setOtp(''); setTempMobileNumber(''); setError(''); }}
                  sx={{ textTransform: 'none', fontSize: '0.82rem', color: '#94a3b8', borderRadius: '8px', '&:hover': { color: '#06b6d4', background: 'rgba(6,182,212,0.07)' } }}
                >
                  Back to Login
                </Button>
              </Box>
            </>
          )}

          {/* Policy footer links */}
          <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'center', gap: 0.5, flexWrap: 'wrap' }}>
            {[
              { label: 'Terms', path: '/terms' },
              { label: 'Privacy', path: '/privacy' },
              { label: 'Refund', path: '/refund' },
            ].map((item, idx, arr) => (
              <React.Fragment key={item.label}>
                <Typography
                  onClick={() => navigate(item.path)}
                  sx={{ fontSize: '0.72rem', color: '#94a3b8', cursor: 'pointer', fontWeight: 500, '&:hover': { color: '#06b6d4' }, transition: 'color 0.2s' }}
                >
                  {item.label}
                </Typography>
                {idx < arr.length - 1 && (
                  <Typography sx={{ fontSize: '0.72rem', color: '#cbd5e1' }}>·</Typography>
                )}
              </React.Fragment>
            ))}
          </Box>
          <Typography sx={{ textAlign: 'center', fontSize: '0.7rem', color: '#cbd5e1', mt: 1 }}>
            © 2026 PayNTric · Developed by <Box component="a" href="https://jcentrix.tech/" target="_blank" rel="noopener noreferrer" sx={{ color: '#06b6d4', textDecoration: 'none', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}>Jcentrix</Box>
          </Typography>

        </Box>{/* end inner scroll wrapper */}
      </Box>
    </Box>
  );
};

export default MuiLogin;
